import React, { useEffect, useRef, useState } from 'react';
import ModelPicker, {
  isEventInsideModelPickerPanel,
  isModelPickerPanelOpen,
} from '../Common/ModelPicker';
import { PROSE_SCENE_GENERATION_KEY } from '../../utils/estimateSceneWritingCost';
import {
  CHECK_BEFORE_ACCEPTING,
  CHOOSE_AI_MODEL,
  NOTES_FOR_THIS_DRAFT,
  SHOW_SCENE_CONTEXT,
  WRITE_MORE,
  WRITE_MORE_VISIBLE,
  WRITE_NEW_DRAFT,
} from '../../utils/writerCopy';

/** Extra Write actions. The model picker saves the prose model on its own. */
export default function WriteMoreMenu({
  onNewDraft,
  onNotes,
  onCheck,
  onContext,
  newDraftDisabled = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      // The model list is portaled to document.body, so a press on a row is
      // outside this ref. Ignoring it lets the row's click save the model.
      if (isEventInsideModelPickerPanel(event)) return;
      if (!ref.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      // The panel is still mounted for this same keydown and closes itself.
      // A later Escape, once the panel is gone, closes the menu.
      if (isModelPickerPanelOpen()) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = (fn) => {
    setOpen(false);
    fn?.();
  };

  return (
    <div className="sw-more" ref={ref}>
      <button
        type="button"
        className="sw-btn"
        aria-label={WRITE_MORE}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        {WRITE_MORE_VISIBLE}
      </button>
      {open && (
        <div className="sw-more__menu" role="menu" aria-label={WRITE_MORE}>
          <button
            type="button"
            role="menuitem"
            className="sw-more__item"
            disabled={newDraftDisabled}
            onClick={() => { if (!newDraftDisabled) run(onNewDraft); }}
          >
            {WRITE_NEW_DRAFT}
          </button>
          <button type="button" role="menuitem" className="sw-more__item" onClick={() => run(onNotes)}>
            {NOTES_FOR_THIS_DRAFT}
          </button>
          <button type="button" role="menuitem" className="sw-more__item" onClick={() => run(onCheck)}>
            {CHECK_BEFORE_ACCEPTING}
          </button>
          <button type="button" role="menuitem" className="sw-more__item" onClick={() => run(onContext)}>
            {SHOW_SCENE_CONTEXT}
          </button>
          <div className="sw-more__model">
            <span className="sw-more__label">{CHOOSE_AI_MODEL}</span>
            <ModelPicker mode="select" functionKey={PROSE_SCENE_GENERATION_KEY} />
          </div>
        </div>
      )}
    </div>
  );
}
